"use client"

import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

export const RAIL_LABEL =
  "text-[9.5px] font-medium uppercase tracking-[0.16em] text-muted-foreground"

/**
 * The context rail beside a takeover: the other comments beside the one being
 * read, the open tasks beside the one being written.
 *
 * Opening one thing should not cost you the sight of the rest, so the rail
 * arrives as early as it can be *read* rather than as early as it can be drawn
 * at full size — 236px from @xl, the canvas's 290 at @2xl. It was @3xl once,
 * which vanished on a 1440 window with Robin open: the record measures 765
 * there, three pixels short.
 *
 * Comments and tasks drew this shell separately; it is one component now.
 */
export function SideRail({
  label,
  count,
  /** Filters or search, between the label and the rows. */
  header,
  children,
  className,
}: {
  label: string
  /** Already formatted — "12", or "3/12" once a search has narrowed it. */
  count?: ReactNode
  header?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "hidden w-[236px] flex-none flex-col border-l px-4 py-5 @xl:flex @2xl:w-[290px] @2xl:px-[22px]",
        className,
      )}
    >
      <div className="flex flex-none items-baseline gap-3">
        <span className={RAIL_LABEL}>{label}</span>
        {count !== undefined && (
          <span className="ml-auto font-mono text-[10px] text-muted-foreground">
            {count}
          </span>
        )}
      </div>

      {header}

      <div className="mt-1.5 min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  )
}
